import type { CSSProperties } from 'react';

export interface GetLayoutStylesParams {
  isFillMode: boolean;
  layout?: 'fill' | 'responsive' | 'intrinsic' | 'fixed' | '';
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  width?: number | string;
  height?: number | string;
  isLegacyMode?: boolean;
}

/**
 * Parses width/height values into numeric form and validates them.
 * Shared by responsive and intrinsic layout calculations.
 */
function parseDimensions(width?: number | string, height?: number | string) {
  const numericWidth = typeof width === 'number' ? width : parseFloat(String(width ?? '0'));
  const numericHeight = typeof height === 'number' ? height : parseFloat(String(height ?? '0'));
  const hasValidWidth = !isNaN(numericWidth) && numericWidth > 0;
  const hasValidHeight = !isNaN(numericHeight) && numericHeight > 0;
  return { numericWidth, numericHeight, hasValidWidth, hasValidHeight };
}

/**
 * Generates layout styles for the image based on layout mode and configuration
 *
 * @param params - Layout configuration parameters
 * @returns CSS properties object for the image element
 *
 * @example
 * ```tsx
 * const styles = getLayoutStyles({
 *   isFillMode: true,
 *   objectFit: 'cover',
 * });
 * // Returns: { position: 'absolute', height: '100%', width: '100%', ... }
 * ```
 */
export function getLayoutStyles(params: GetLayoutStylesParams): CSSProperties {
  const { isFillMode, objectFit, objectPosition, width, height, layout, isLegacyMode } = params;
  const styles: CSSProperties = {};

  // Handle fill mode
  if (isFillMode) {
    return {
      ...styles,
      position: 'absolute',
      height: '100%',
      width: '100%',
      left: '0px',
      top: '0px',
      right: '0px',
      bottom: '0px',
      color: 'transparent',
      ...(isLegacyMode && {
        objectFit: objectFit,
        objectPosition: objectPosition,
      }),
    };
  }

  if (isLegacyMode) {
    // Apply deprecated objectFit and objectPosition if provided
    if (objectFit) {
      styles.objectFit = objectFit;
    }
    if (objectPosition) {
      styles.objectPosition = objectPosition;
    }

    // Handle responsive layout for next/legacy/image
    // Uses CSS aspect-ratio for consistent height and aspect ratio if both width and height are provided.
    if (layout === 'responsive') {
      const { numericWidth, numericHeight, hasValidWidth, hasValidHeight } = parseDimensions(
        width,
        height
      );

      styles.width = '100%';
      styles.height = 'auto';
      styles.position = 'relative';

      if (hasValidHeight && hasValidWidth) {
        styles.aspectRatio = `${numericWidth} / ${numericHeight}`;
      }
    }

    // Handle fixed layout for next/legacy/image
    if (layout === 'fixed') {
      if (width !== undefined) styles.width = width;
      if (height !== undefined) styles.height = height;
    }

    // Handle intrinsic layout - mirrors next/legacy/image intrinsic behavior without
    // the wrapper span and SVG sizer.
    // when it's in legacymode, layout should defrault to intrinsic when layout isn't
    // explicitly declared
    if (layout === 'intrinsic' || !layout) {
      const { numericWidth, numericHeight, hasValidWidth, hasValidHeight } = parseDimensions(
        width,
        height
      );

      styles.height = 'auto';
      styles.maxWidth = '100%';

      if (hasValidWidth) {
        styles.width = numericWidth;
      }

      if (hasValidWidth && hasValidHeight) {
        styles.aspectRatio = `${numericWidth} / ${numericHeight}`;
      }
    }
  }

  return styles;
}
